import { api } from "@convex/_generated/api";
import { Blob, Button, Card, CardHeader, CardTitle, ChevronRightIcon, PageHeading, PlusIcon, RowText, SectionLabel, Tag, Tray, TrayRow } from "@buddy/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { useUi } from "@/app/ui-state";
import { modelLabel } from "@/features/chat/format";
import { EmployeeCard } from "@/features/employee/employee-card";
import { errorText } from "@/features/employee/errors";
import { budgetLine, shortDate } from "@/features/employee/labels";
import { MoneyOf } from "@/features/employee/money";
import { billsInDollars } from "@buddy/core";
import { useOrg } from "@/lib/org";

export const Route = createFileRoute("/_app/_main/team/")({ component: Team });

// The roster: a quiet tray of everyone on the team, then anyone who was let go with a
// way back. Only exceptions get a tag; a healthy employee shows nothing extra.
function Team() {
  const org = useOrg();
  const { openHire } = useUi();
  const employees = useQuery(api.employees.list, { orgId: org.id, includeFired: true });
  const rehire = useMutation(api.employees.rehire);
  const [error, setError] = useState<string | null>(null);
  const admin = org.role === "owner" || org.role === "admin";
  const active = (employees ?? []).filter((employee) => !employee.fired).toSorted((a, b) => a.name.localeCompare(b.name));
  const gone = (employees ?? []).filter((employee) => employee.fired);

  return (
    <Card className="flex-1">
      <CardHeader
        actions={
          admin && (
            <Button variant="primary" size="sm" onClick={openHire}>
              <PlusIcon />
              Hire
            </Button>
          )
        }
      >
        <CardTitle>Team</CardTitle>
      </CardHeader>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex w-full max-w-[916px] flex-col gap-5 px-12 pb-8 pt-7">
          <PageHeading
            title={`${org.name}'s employees`}
            caption="Open someone to see what they remember, what they may do alone and what they cost."
          />
          <div className="flex flex-col gap-2">
            <SectionLabel note={employees === undefined ? undefined : `${active.length} on the team`}>Employees</SectionLabel>
            <Tray>
              {employees !== undefined && active.length === 0 && (
                <TrayRow height={60}>
                  <RowText title="Nobody yet" caption="Hire your first employee to hand them recurring work." />
                  {admin && (
                    <Button variant="primary" size="sm" onClick={openHire}>
                      Hire
                    </Button>
                  )}
                </TrayRow>
              )}
              {active.map((employee) => (
                <TrayRow key={employee._id} height={60} interactive>
                  <EmployeeCard
                    employeeId={employee._id}
                    trigger={
                      <button type="button" aria-label={`${employee.name} at a glance`} className="flex shrink-0 rounded-full">
                        <Blob hue={employee.hue} face={employee.face} size={32} />
                      </button>
                    }
                  />
                  <Link
                    to="/team/$employeeId/memory"
                    params={{ employeeId: employee._id }}
                    className="flex min-w-0 flex-1 items-center gap-3.5 self-stretch"
                  >
                    <RowText
                      title={employee.name}
                      caption={[employee.title || null, `@${employee.handle}`, modelLabel(employee.model)]
                        .filter((part) => part !== null)
                        .join(" · ")}
                    />
                    {employee.status === "paused" && (
                      <Tag tone="attention">
                        {employee.pauseReason === "budget" ? "Out of budget" : employee.pauseReason === "org_budget" ? "Team at its cap" : "Paused"}
                      </Tag>
                    )}
                    {employee.status === "needs_you" && <Tag tone="attention">Needs you</Tag>}
                    <span className="w-28 shrink-0 text-right text-[12px] leading-4 tracking-normal text-text-2 tabular">
                      {billsInDollars(employee) ? (
                        <MoneyOf spentUsd={employee.spentUsdThisMonth} capUsd={employee.monthlyCapUsd ?? null} />
                      ) : (
                        budgetLine(employee.tokensUsedThisMonth, employee.monthlyTokenBudget)
                      )}
                    </span>
                    <ChevronRightIcon className="size-3.5 shrink-0 text-text-3" />
                  </Link>
                </TrayRow>
              ))}
            </Tray>
          </div>

          {gone.length > 0 && (
            <div className="flex flex-col gap-2">
              <SectionLabel note="Their notes and chats are kept. Rehire brings them back as they were.">Let go</SectionLabel>
              <Tray quiet>
                {gone.map((employee) => (
                  <TrayRow key={employee._id} height={52} quiet>
                    <Blob hue={employee.hue} face={employee.face} size={28} />
                    <RowText
                      title={employee.name}
                      caption={`${employee.title ? `${employee.title} · ` : ""}let go ${shortDate(employee.firedAt ?? 0)}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      render={<Link to="/team/$employeeId/memory" params={{ employeeId: employee._id }} />}
                      nativeButton={false}
                    >
                      View
                    </Button>
                    {admin && (
                      <Button
                        size="sm"
                        onClick={() => {
                          setError(null);
                          rehire({ orgId: org.id, employeeId: employee._id }).catch((cause: unknown) =>
                            setError(errorText(cause)),
                          );
                        }}
                      >
                        Rehire
                      </Button>
                    )}
                  </TrayRow>
                ))}
              </Tray>
              {error !== null && <p className="px-0.5 text-[12px] leading-4 text-negative">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
